// Bounded, account-local discovery history stores identifiers, never chat text.
export const DISCOVERY_HISTORY_LIMIT = 500;
export function ideaKey(idea) {
  if (idea.id && !/^(memory|project)-\d+$/.test(idea.id)) return String(idea.id).slice(0, 160);
  let hash = 2166136261;
  for (const char of `${idea.title || idea.label || ''}|${idea.prompt || ''}`) hash = Math.imul(hash ^ char.charCodeAt(0), 16777619) >>> 0;
  return `idea-${hash.toString(16)}`;
}
export function cleanDiscoveryHistory(value, now = Date.now()) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  return Object.fromEntries(Object.entries(value).filter(([key, row]) => key && row &&
    (row.seen == null || Number.isFinite(row.seen)) && (row.used == null || Number.isFinite(row.used)) && Math.max(row.seen || 0, row.used || 0) > now - 90 * 86400000)
    .sort((a, b) => Math.max(b[1].seen || 0, b[1].used || 0) - Math.max(a[1].seen || 0, a[1].used || 0))
    .slice(0, DISCOVERY_HISTORY_LIMIT).map(([key, row]) => [key, { seen: Number(row.seen) || 0, used: Number(row.used) || 0 }]));
}
const words = title => new Set(String(title || '').toLowerCase().split(/\W+/).filter(word => word.length > 3));
export function rankDiscoveryIdeas(ideas, history = {}) {
  const distinct = [];
  for (const idea of ideas) {
    const topic = words(idea.title || idea.label);
    if (!distinct.some(previous => {
      const other = words(previous.title || previous.label);
      const overlap = [...topic].filter(word => other.has(word)).length;
      return overlap >= 3 && overlap / Math.max(1, Math.max(topic.size, other.size)) >= 0.75;
    })) distinct.push(idea);
  }
  const remaining = [...distinct], result = [];
  const novelty = idea => history[ideaKey(idea)]?.used ? 20 : history[ideaKey(idea)]?.seen ? 10 : 0;
  while (remaining.length) {
    let best = 0, score = Infinity;
    remaining.forEach((idea, i) => {
      const topic = words(idea.title || idea.label);
      const similar = result.slice(-3).some(previous => {
        const other = words(previous.title || previous.label);
        return [...topic].filter(word => other.has(word)).length / Math.max(1, Math.min(topic.size, other.size)) > 0.6;
      });
      const sameFormat = result.length && (idea.feature || idea.icon) === (result[result.length - 1].feature || result[result.length - 1].icon);
      const next = novelty(idea) + (similar ? 4 : 0) + (sameFormat ? 2 : 0) + i / 10000;
      if (next < score) { score = next; best = i; }
    });
    result.push(remaining.splice(best, 1)[0]);
  }
  return result;
}
export function recentDiscoveryIdeas(chats = [], mode = 'chat', now = Date.now()) {
  return chats.filter(chat => chat.isOwner === true && !chat.isArchived && !chat.isPlusLocked && !chat.inCollectionId &&
    !chat.hasMacProject && !['running', 'queued'].includes(chat.coworkerSession?.status) && !chat.flags?.isTemporary && !chat.flags?.isDeleted && !chat.isInferencing && !chat.isInferencingWaiting &&
    Boolean(chat.flags?.isCoworkerSession || chat.coworkerSession) === (mode === 'work') &&
    typeof chat.title === 'string' && chat.title.trim() && chat.title !== 'Untitled Chat' &&
    now - new Date(chat.updatedAt || chat.createdAt).getTime() < 30 * 86400000)
    .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)).slice(0, 3)
    .map(chat => ({ id: `continue-${chat.id}`, conversationId: chat.id, description: chat.title.slice(0, 120),
      updatedAt: chat.updatedAt || chat.createdAt, feature: 'Continue', icon: 'arrow-right' }));
}
